import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { getDeletedClients, restoreClient } from "../api/api";
import Pagination from "../components/Pagination";
import "./Client.css";

function getInitials(name = "") {
  return (name || "").split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
}

const statusLabel = { active: "Active", inactive: "Inactive", suspended: "Suspended" };
const normalizeClient = (c = {}) => {
  const phoneFromContact = c.contactInfo?.find(x => x.type === "phone")?.value || "";
  return {
    _id:         c._id || c.id,
    name:        c.name || "",
    email:       c.email || "",
    phone:       c.phone || phoneFromContact,
    company:     c.company || "",
    status:      c.status || "active",
    contactInfo: c.contactInfo || [],
  };
};

const SOCIAL_FIELDS = ["facebook","instagram","whatsapp","tiktok","linkedin","snapchat"];

const SocialIcons = {
  facebook:  { icon: "f",  color: "#3b82f6", bg: "#1d3461" },
  instagram: { icon: "📷", color: "#e1306c", bg: "#3d1020" },
  whatsapp:  { icon: "💬", color: "#25d366", bg: "#0d2e1a" },
  tiktok:    { icon: "♪",  color: "#ffffff", bg: "#1a1a1a" },
  linkedin:  { icon: "in", color: "#0a66c2", bg: "#0a2540" },
  snapchat:  { icon: "👻", color: "#fffc00", bg: "#2a2800" },
};

export default function DeletedClients() {
  const navigate = useNavigate();

  const [clients,    setClients]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [search,     setSearch]     = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => { fetchDeletedClients(); }, []);

  const fetchDeletedClients = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await getDeletedClients();
      const data = res?.data?.data ?? res?.data ?? res ?? [];
      setClients(Array.isArray(data) ? data.map(normalizeClient) : []);
    } catch {
      setError("Failed to load deleted clients");
    } finally {
      setLoading(false);
    }
  };

  const filtered = clients.filter(c =>
    [c.name, c.email, c.company, c.phone].some(f =>
      (f || "").toLowerCase().includes(search.toLowerCase())
    )
  );

  // Pagination
  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginatedClients = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Reset page when search changes
  useEffect(() => { setCurrentPage(1); }, [search]);

  const handleRestore = async (e, id) => {
    e.stopPropagation();
    const confirmed = window.confirm("Are you sure you want to restore this client?");
    if (!confirmed) return;
    try {
      await restoreClient(id);
      setClients(prev => prev.filter(c => c._id !== id));
      toast.success("Client restored successfully");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to restore client");
    }
  };

  /* ── Render ── */
  if (loading) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", minHeight:"60vh" }}>
      <div style={{ color:"#4b5563", fontSize:15 }}>Loading…</div>
    </div>
  );

  if (error) return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", minHeight:"60vh", gap:12 }}>
      <div style={{ color:"#f87171", fontSize:15 }}>{error}</div>
      <button onClick={fetchDeletedClients} style={{ padding:"10px 24px", background:"#facc15", color:"#0a0f1a", border:"none", borderRadius:8, fontWeight:700, cursor:"pointer" }}>
        Retry
      </button>
    </div>
  );

  return (
    <div className="clients-page">

      {/* Header */}
      <div className="clients-header">
        <div>
          <h1>Deleted Clients</h1>
          <p>{clients.length} deleted clients</p>
        </div>
        <button className="add-client-btn" onClick={() => navigate("/clients")}>
          ← Back to Clients
        </button>
      </div>

      {/* Search */}
      <div className="search-bar">
        <span className="search-icon">🔍</span>
        <input
          placeholder="Search by name, email or company…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {/* Table */}
      <div className="clients-table-wrapper">
        <table className="clients-table">
          <thead>
            <tr>
              <th>Client</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Social</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={6}><div className="empty-state"><p>No deleted clients found.</p></div></td></tr>
            ) : (
              paginatedClients.map(client => (
                <tr key={client._id}>
                  <td>
                    <div className="client-name-cell">
                      <div className="client-avatar">{getInitials(client.name)}</div>
                      <div>
                        <div className="client-name-text">{client.name}</div>
                        <div className="client-company">{client.company}</div>
                      </div>
                    </div>
                  </td>
                  <td>{client.email}</td>
                  <td>{client.phone}</td>
                  <td>
                    {/* Social mini-icons */}
                    <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
                      {(client.contactInfo || []).filter(c => c.value).map(c => {
                        const s = SocialIcons[c.type];
                        if (!s) return null;
                        return (
                          <a
                            key={c.type}
                            href={c.type === "whatsapp" ? `https://wa.me/${c.value}` : c.value}
                            target="_blank"
                            rel="noreferrer"
                            onClick={e => e.stopPropagation()}
                            title={c.type}
                            style={{
                              width:26, height:26, borderRadius:6,
                              background: s.bg, color: s.color,
                              display:"flex", alignItems:"center", justifyContent:"center",
                              fontSize:11, fontWeight:800,
                              border:`1px solid ${s.color}30`,
                              textDecoration:"none", flexShrink:0,
                            }}
                          >
                            {s.icon}
                          </a>
                        );
                      })}
                    </div>
                  </td>
                  <td>
                    <span className={`status-badge status-${client.status}`}>
                      {statusLabel[client.status] || client.status}
                    </span>
                  </td>
                  <td>
                    <div className="table-actions" onClick={e => e.stopPropagation()}>
                      <button className="action-btn" onClick={e => handleRestore(e, client._id)} style={{ background: "#0a2318", color: "#34d399", borderColor: "#34d39928" }}>
                        Restore
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onChange={setCurrentPage}
      />
    </div>
  );
}
